import React, { useContext, useEffect, useMemo, useState } from 'react';
import UserContext from '../../../context/UserContext.js';
import * as DTOs from '../../DTOs/PartidoFixtureDTOs.js';
import * as FuncionesFixture from '../../funciones/FuncionesFixture.js';
import MensajeCargando from '../../UI/MensajeCargando.js';
import MensajeError from '../../UI/MensajeError.js';
import MensajeGrabando from '../../UI/MensajeGrabando.js';
import ElegirFechaEtapa from './ElegirFechaEtapa.js';
import PreviewFixtureAutomatico from './PreviewFixtureAutomatico.js';
import TablaFechaFixtureGrupos from './TablaFechaFixtureGrupos.js';
import api from '../../../api/client.js';
import useGetListaURL from '../../../hooks/useGetListaURL.js';
import usePatchURL from '../../../hooks/usePatchURL.js';
import usePostURL from '../../../hooks/usePostURL.js';
import useEdicionFilaPartido from '../../../hooks/useEdicionFilaPartido.js';
import styles from './FixtureEtapaGrupos.module.css';

const PRIMERA_FECHA = 1;

const FixtureEtapaGrupos = (props) => {
  const datosElegidos = props.datosElegidos;
  const onPartidoElegido = props.onPartidoElegido; // (idPartido) => void - navega al partido
  const etapa = datosElegidos?.etapa;
  const torneo = datosElegidos?.torneo;
  const { usuario } = useContext(UserContext);
  const puedeEditar = usuario && usuario.puedeEditar;

  const [partidosExistentes, setPartidosExistentes] = useState([]);
  const [partidosFaltantes, setPartidosFaltantes] = useState([]);
  const [partidosFaltantesPorFecha, setPartidosFaltantesPorFecha] = useState({});
  const [equiposDisponibles, setEquiposDisponibles] = useState([]);
  const [modoFixture, setModoFixture] = useState('carga'); // carga | manual | automatico
  const [fechaSeleccionadaManual, setFechaSeleccionadaManual] = useState(1);
  const [fixtureAutomatico, setFixtureAutomatico] = useState([]);
  const [isGenerandoPreview, setIsGenerandoPreview] = useState(false);
  const [isConfirmandoFixture, setIsConfirmandoFixture] = useState(false);
  const [erroresFixture, setErroresFixture] = useState(null);
  const [isLoadingEtapa, setIsLoadingEtapa] = useState(false);

  const { isCargando, erroresGetLista, fetchData, resetErroresGetLista } = useGetListaURL();
  const { isGrabando, erroresPost, postData, resetErroresPost } = usePostURL();
  const { isGrabando: isGrabandoPatch, erroresPatch, patchData, resetErroresPatch } = usePatchURL();
  const edicion = useEdicionFilaPartido({ erroresPost, erroresPatch });
  const { partidosEnEdicion, partidoGrabandose, erroresFilaPartido } = edicion;

  const mostrarZona = etapa?.zonas === true;

  const cantidadFechasTotales = useMemo(() => {
    // cantidadFechas ya representa el total de fechas en la etapa de grupos
    return etapa?.cantidadFechas || 0;
  }, [etapa]);

  const partidosFechaUno = useMemo(() => {
    return (partidosExistentes || []).filter(
      (partido) => Number(partido.nroFecha) === PRIMERA_FECHA
    );
  }, [partidosExistentes]);

  const fechaUnoCompleta = useMemo(() => {
    if (!etapa?.cantidadCruces) {
      return false;
    }

    if (partidosFechaUno.length !== etapa.cantidadCruces) {
      return false;
    }

    return partidosFechaUno.every((partido) => {
      if (!FuncionesFixture.TieneRivalesValidos(partido)) {
        return false;
      }
      if (mostrarZona) {
        return !!FuncionesFixture.NormalizarZona(partido.zona);
      }
      return true;
    });
  }, [etapa, partidosFechaUno, mostrarZona]);

  const todasLasFechasCompletas = useMemo(() => {
    // Si existen partidos de fechas diferentes a la 1, significa que fueron cargadas
    const tienePartidosFueraFechaUno = (partidosExistentes || []).some(
      (partido) => Number(partido.nroFecha) > PRIMERA_FECHA
    );
    return tienePartidosFueraFechaUno;
  }, [partidosExistentes]);

  const fechaTrabajo =
    modoFixture === 'manual' || todasLasFechasCompletas ? fechaSeleccionadaManual : PRIMERA_FECHA;

  const partidosFechaActual = useMemo(() => {
    return (partidosExistentes || []).filter(
      (partido) => Number(partido.nroFecha) === Number(fechaTrabajo)
    );
  }, [partidosExistentes, fechaTrabajo]);

  useEffect(() => {
    resetErroresGetLista();
    // Al cambiar etapa, solo marcar que estamos cargando
    // NO limpiar datos aún, solo esperar a que lleguen
    if (torneo && etapa && etapa.etapa) {
      setIsLoadingEtapa(true);
      const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
      fetchData(url, (partidos) => {
        // Aquí llegan datos nuevos, recién entonces actualizar TODO
        setPartidosExistentes(partidos || []);
        setPartidosFaltantesPorFecha({});
        setPartidosFaltantes([]);
        setModoFixture('carga');
        setFechaSeleccionadaManual(1);
        setFixtureAutomatico([]);
        setErroresFixture(null);
        setIsLoadingEtapa(false);
      });
      return;
    }

    setPartidosExistentes([]);
    setIsLoadingEtapa(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [torneo, etapa, fetchData, resetErroresGetLista]);

  useEffect(() => {
    if (!torneo) {
      setEquiposDisponibles([]);
      return;
    }

    fetchData(`torneos/${torneo.id}/participantes`, (participantes) => {
      const equipos = (participantes || []).map((p) => p.equipo).filter((e) => e != null);
      setEquiposDisponibles(equipos);
    });
  }, [torneo, fetchData]);

  useEffect(() => {
    if (!etapa || !datosElegidos) {
      setPartidosFaltantes([]);
      setPartidosFaltantesPorFecha({});
      setModoFixture('carga');
      setFechaSeleccionadaManual(1);
      setFixtureAutomatico([]);
      setErroresFixture(null);
      return;
    }

    const fechaActual =
      modoFixture === 'manual' || todasLasFechasCompletas ? fechaSeleccionadaManual : PRIMERA_FECHA;
    const borradorGuardado = partidosFaltantesPorFecha[fechaActual];

    if (borradorGuardado) {
      setPartidosFaltantes(borradorGuardado);
      return;
    }

    const faltantes = FuncionesFixture.PartidosFaltantesGrupos(
      partidosExistentes,
      datosElegidos,
      fechaActual
    );
    const faltantesConOrdinales = asignarOrdinalesToPartidos(faltantes);
    setPartidosFaltantes(faltantesConOrdinales);
  }, [
    partidosExistentes,
    etapa,
    datosElegidos,
    fechaSeleccionadaManual,
    modoFixture,
    partidosFaltantesPorFecha,
    todasLasFechasCompletas,
  ]);

  const recargarPartidos = () => {
    setIsLoadingEtapa(true);
    const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
    fetchData(url, (partidos) => {
      setPartidosExistentes(partidos || []);
      setPartidosFaltantesPorFecha({});
      setPartidosFaltantes([]);
      setModoFixture('carga');
      setFechaSeleccionadaManual(1);
      setFixtureAutomatico([]);
      setErroresFixture(null);
      setIsLoadingEtapa(false);
    });
  };

  const asignarOrdinalesToPartidos = (partidos) => {
    return (partidos || []).map((partido, index) => ({
      ...partido,
      ordenPartido: partido.ordenPartido || index + 1,
      orden: partido.orden || index + 1,
    }));
  };

  const normalizarPartidoParaGuardar = (partido) => {
    return {
      ...partido,
      zona: FuncionesFixture.NormalizarZona(partido.zona),
      equipoLibre: partido.equipoLibre === true,
      equipoVisitante: partido.equipoLibre === true ? null : partido.equipoVisitante,
      nroFecha: PRIMERA_FECHA,
      codigoInstancia: null,
      observaciones: null,
      dudas: null,
    };
  };

  const onPartidoChange = (idPartido, partidoPatch) => {
    setPartidosFaltantes((prev) => {
      const nuevosPartidos = [...prev];
      const index = nuevosPartidos.findIndex((p) => p.idPartido === idPartido);
      if (index === -1) {
        return prev;
      }

      const partidoActual = nuevosPartidos[index] || {};
      nuevosPartidos[index] = {
        ...partidoActual,
        ...partidoPatch,
      };

      // Si se cambió la zona (incluyendo borrar), propagarla a todos los partidos siguientes
      if (partidoPatch.zona !== undefined) {
        const zonaNormalizada = FuncionesFixture.NormalizarZona(partidoPatch.zona);
        for (let i = index + 1; i < nuevosPartidos.length; i++) {
          if (nuevosPartidos[i]) {
            nuevosPartidos[i] = {
              ...nuevosPartidos[i],
              zona: zonaNormalizada,
            };
          }
        }
      }

      setPartidosFaltantesPorFecha((prevPorFecha) => ({
        ...prevPorFecha,
        [fechaTrabajo]: nuevosPartidos,
      }));
      return nuevosPartidos;
    });
  };

  const onCancelarPartido = (partido) => {
    const nuevoPartido = DTOs.PartidoFixtureVacio(
      datosElegidos,
      partido.idPartido,
      null,
      partido.indiceCruce,
      null,
      PRIMERA_FECHA
    );

    const indexEnFaltantes = partidosFaltantes.findIndex((p) => p.idPartido === partido.idPartido);
    if (indexEnFaltantes === -1) {
      return;
    }

    const nuevosPartidos = [...partidosFaltantes];
    nuevosPartidos[indexEnFaltantes] = nuevoPartido;
    setPartidosFaltantes(nuevosPartidos);
    setPartidosFaltantesPorFecha((prev) => ({
      ...prev,
      [fechaTrabajo]: nuevosPartidos,
    }));
  };

  const onEditarPartido = (idPartido) => {
    const partido = partidosFechaActual.find((p) => p.id === idPartido);
    if (!partido) {
      return;
    }

    edicion.iniciarEdicion(partido, { zona: FuncionesFixture.NormalizarZona(partido.zona) });
  };

  const onEditarTodos = () => {
    edicion.iniciarEdicionMultiple(partidosFechaActual, (partido) => ({
      zona: FuncionesFixture.NormalizarZona(partido.zona),
    }));
  };

  const onEliminarPartido = (idPartido) => {
    edicion.eliminarPartido(idPartido, recargarPartidos);
  };

  const onGuardarPartido = (partido) => {
    const errorBase = FuncionesFixture.ValidarPartidoFixture(partido, {
      requiereFecha: false,
      zonaObligatoria: mostrarZona,
      permiteEquipoLibre: true,
    });
    if (errorBase) {
      alert(errorBase);
      return;
    }

    const errorRepetidosNuevos = FuncionesFixture.ValidarEquiposRepetidos(
      [partido],
      partidosFaltantes.filter(
        (p) =>
          p.idPartido !== partido.idPartido &&
          p.anioPartido &&
          FuncionesFixture.TieneRivalesValidos(p)
      )
    );
    if (errorRepetidosNuevos) {
      alert(errorRepetidosNuevos);
      return;
    }

    const errorRepetidosExistentes = FuncionesFixture.ValidarEquiposRepetidos(
      [partido],
      partidosFechaActual
    );
    if (errorRepetidosExistentes) {
      alert(errorRepetidosExistentes);
      return;
    }

    resetErroresPost();
    edicion.limpiarErrorFila(partido.idPartido);
    edicion.setPartidoGrabandose(partido.idPartido);

    const dto = DTOs.SavePartidoFixture(normalizarPartidoParaGuardar(partido));
    postData(`torneos/${torneo.id}/partidos`, [dto], () => {
      edicion.setPartidoGrabandose(null);
      edicion.limpiarErrorFila(partido.idPartido);
      setPartidosFaltantes((prev) => prev.filter((p) => p.idPartido !== partido.idPartido));
      setPartidosFaltantesPorFecha((prev) => ({
        ...prev,
        [fechaTrabajo]: prev[fechaTrabajo]?.filter((p) => p.idPartido !== partido.idPartido) || [],
      }));
      recargarPartidos();
    });
  };

  const onGuardarEdicion = (partido) => {
    const errorBase = FuncionesFixture.ValidarPartidoFixture(partido, {
      requiereFecha: false,
      zonaObligatoria: mostrarZona,
      permiteEquipoLibre: true,
    });
    if (errorBase) {
      alert(errorBase);
      return;
    }

    const otrosExistentes = partidosFechaActual.filter((p) => p.id !== partido.id);
    const errorRepetidosExistentes = FuncionesFixture.ValidarEquiposRepetidos(
      [partido],
      otrosExistentes
    );
    if (errorRepetidosExistentes) {
      alert(errorRepetidosExistentes);
      return;
    }

    const errorRepetidosNuevos = FuncionesFixture.ValidarEquiposRepetidos(
      [partido],
      partidosFaltantes.filter((p) => p.anioPartido && FuncionesFixture.TieneRivalesValidos(p))
    );
    if (errorRepetidosNuevos) {
      alert(errorRepetidosNuevos);
      return;
    }

    resetErroresPatch();
    edicion.limpiarErrorFila(partido.id);
    edicion.setPartidoGrabandose(partido.id);

    patchData(
      `partidos/${partido.id}`,
      DTOs.PatchPartidoFixture(normalizarPartidoParaGuardar(partido)),
      () => {
        edicion.setPartidoGrabandose(null);
        edicion.limpiarErrorFila(partido.id);
        recargarPartidos();
        edicion.cancelarEdicion(partido.id);
      }
    );
  };

  const onGuardarTodos = () => {
    const partidosNuevosValidos = partidosFaltantes
      .map(normalizarPartidoParaGuardar)
      .filter((p) => FuncionesFixture.TieneRivalesValidos(p) && (!mostrarZona || p.zona));

    const partidosEditadosValidos = Object.values(partidosEnEdicion)
      .map(normalizarPartidoParaGuardar)
      .filter(
        (p) => p && p.id && FuncionesFixture.TieneRivalesValidos(p) && (!mostrarZona || p.zona)
      );

    const totalParaGuardar = partidosNuevosValidos.length + partidosEditadosValidos.length;
    if (totalParaGuardar === 0) {
      alert('No hay partidos válidos para guardar');
      return;
    }

    const partidosConErrorBase = [...partidosNuevosValidos, ...partidosEditadosValidos].find(
      (partido) =>
        FuncionesFixture.ValidarPartidoFixture(partido, {
          requiereFecha: false,
          zonaObligatoria: mostrarZona,
          permiteEquipoLibre: true,
        })
    );
    if (partidosConErrorBase) {
      alert(
        FuncionesFixture.ValidarPartidoFixture(partidosConErrorBase, {
          requiereFecha: false,
          zonaObligatoria: mostrarZona,
          permiteEquipoLibre: true,
        })
      );
      return;
    }

    const idsEditados = new Set(partidosEditadosValidos.map((p) => p.id));
    const referenciaExistentes = partidosFechaActual.filter((p) => !idsEditados.has(p.id));
    const errorRepetidos = FuncionesFixture.ValidarEquiposRepetidos(
      [...partidosNuevosValidos, ...partidosEditadosValidos],
      referenciaExistentes
    );
    if (errorRepetidos) {
      alert(errorRepetidos);
      return;
    }

    const mensaje = [];
    if (partidosNuevosValidos.length > 0) {
      mensaje.push(`crear ${partidosNuevosValidos.length} partido(s)`);
    }
    if (partidosEditadosValidos.length > 0) {
      mensaje.push(`actualizar ${partidosEditadosValidos.length} partido(s)`);
    }

    const confirmar = window.confirm(`¿Está seguro que desea ${mensaje.join(' y ')}?`);
    if (!confirmar) {
      return;
    }

    if (partidosNuevosValidos.length > 0) {
      resetErroresPost();
      const dtosNuevos = partidosNuevosValidos.map((partido) => DTOs.SavePartidoFixture(partido));
      postData(`torneos/${torneo.id}/partidos`, dtosNuevos, () => {
        const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
        fetchData(url, (partidosActualizados) => {
          const listaActualizada = partidosActualizados || [];
          setPartidosExistentes(listaActualizada);

          const noGuardados = partidosNuevosValidos.filter(
            (partido) =>
              !listaActualizada.some((existente) =>
                FuncionesFixture.CoincideConPartidoGrabado(partido, existente)
              )
          );

          setPartidosFaltantes(noGuardados);
          setPartidosFaltantesPorFecha((prev) => ({
            ...prev,
            [fechaTrabajo]: noGuardados,
          }));
          // Resetear al modo carga después de grabar exitosamente
          if (noGuardados.length === 0) {
            setModoFixture('carga');
          }
        });
      });
    }

    if (partidosEditadosValidos.length > 0) {
      resetErroresPatch();
      const dtosEditados = partidosEditadosValidos.map((partido) => ({
        idPartido: partido.id,
        ...DTOs.PatchPartidoFixture(partido),
      }));
      patchData(`torneos/${torneo.id}/partidos`, dtosEditados, () => {
        recargarPartidos();
        edicion.limpiarEdicionMultiple(partidosEditadosValidos.map((p) => p.id));
        // Resetear al modo carga después de grabar exitosamente
        setModoFixture('carga');
      });
    }
  };

  const onCambiarModoFixture = (modoNuevo) => {
    setErroresFixture(null);
    setFixtureAutomatico([]);
    setModoFixture(modoNuevo);
    if (modoNuevo === 'manual') {
      setFechaSeleccionadaManual((valorActual) => valorActual || 1);
    }
  };

  const onFechaManualElegida = (fecha) => {
    setFechaSeleccionadaManual(Number(fecha));
  };

  const onFixtureAutomatico = async () => {
    setIsGenerandoPreview(true);
    setErroresFixture(null);

    try {
      const primeraFecha = FuncionesFixture.OrdenarPreviewFixture(partidosFechaUno).map(
        (partido) => ({
          idEquipoLocal: partido.equipoLocal?.id || null,
          idEquipoVisitante: partido.equipoVisitante?.id || null,
          zona: FuncionesFixture.NormalizarZona(partido.zona),
          orden: partido.ordenPartido || partido.orden || null,
          equipoLibre: partido.equipoLibre || false,
        })
      );

      const response = await api.post(
        `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/fixture/preview`,
        primeraFecha
      );

      if (!response.ok) {
        setErroresFixture(response.errors || ['No se pudo generar el preview del fixture']);
        return;
      }

      setFixtureAutomatico(response.data || []);
      setModoFixture('automatico');
    } catch (error) {
      setErroresFixture([error.message || 'No se pudo generar el preview del fixture']);
    } finally {
      setIsGenerandoPreview(false);
    }
  };

  const onConfirmarFixtureAutomatico = async () => {
    setIsConfirmandoFixture(true);
    setErroresFixture(null);

    try {
      const body = FuncionesFixture.OrdenarPreviewFixture(fixtureAutomatico).map((partido) => ({
        codigoCategoria: datosElegidos.categoria?.codigo || null,
        codigoEtapa: etapa.etapa?.codigo || null,
        nroFecha: partido.nroFecha || null,
        ordenPartido: partido.orden || partido.ordenPartido || null,
        idEquipoLocal: partido.idEquipoLocal || null,
        idEquipoVisitante: partido.idEquipoVisitante || null,
        zona: FuncionesFixture.NormalizarZona(partido.zona),
        equipoLibre: partido.equipoLibre || false,
      }));

      const response = await api.post(
        `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/fixture`,
        body
      );
      if (!response.ok) {
        setErroresFixture(response.errors || ['No se pudo guardar el fixture automático']);
        return;
      }

      recargarPartidos();
      setModoFixture('carga');
      setFixtureAutomatico([]);
      setPartidosFaltantesPorFecha({});
    } catch (error) {
      setErroresFixture([error.message || 'No se pudo guardar el fixture automático']);
    } finally {
      setIsConfirmandoFixture(false);
    }
  };

  if (!etapa || etapa.definicion === true) {
    return null;
  }

  // Mostrar solo "Cargando" mientras se cargan datos de una nueva etapa
  if (isLoadingEtapa) {
    return (
      <div className={styles.contenedor}>
        <MensajeCargando isCargando={true} />
      </div>
    );
  }

  return (
    <div className={styles.contenedor}>
      {modoFixture === 'carga' && fechaUnoCompleta && !todasLasFechasCompletas && (
        <div className={styles.accionesModo}>
          <button
            className={styles.btnModoFixture}
            onClick={onFixtureAutomatico}
            disabled={isGenerandoPreview}
          >
            Fixture automático
          </button>
          <button
            className={styles.btnModoFixtureSecundario}
            onClick={() => onCambiarModoFixture('manual')}
            disabled={isGenerandoPreview}
          >
            Fixture manual
          </button>
        </div>
      )}

      {modoFixture === 'automatico' && (
        <PreviewFixtureAutomatico
          fixtureAutomatico={fixtureAutomatico}
          mostrarZona={mostrarZona}
          isConfirmando={isConfirmandoFixture}
          onConfirmar={onConfirmarFixtureAutomatico}
          onVolver={() => onCambiarModoFixture('carga')}
        />
      )}

      {(modoFixture === 'manual' || todasLasFechasCompletas) && cantidadFechasTotales > 0 && (
        <div className={styles.grupo}>
          <ElegirFechaEtapa
            etapa={{ ...etapa, cantidadFechas: cantidadFechasTotales }}
            fechaElegida={fechaSeleccionadaManual}
            onFechaElegida={onFechaManualElegida}
          />
          {modoFixture === 'manual' && (
            <div className={styles.accionesModo}>
              <button
                className={styles.btnModoFixtureSecundario}
                onClick={() => onCambiarModoFixture('carga')}
                disabled={isGenerandoPreview}
              >
                Volver
              </button>
            </div>
          )}
        </div>
      )}

      {puedeEditar &&
        (modoFixture === 'carga' || todasLasFechasCompletas) &&
        partidosFechaActual.length > 0 && (
          <div className={styles.accionesEditarTodos}>
            <button onClick={onEditarTodos} className={styles.btnEditarTodos}>
              Editar todos
            </button>
          </div>
        )}

      {modoFixture !== 'automatico' && (
        <TablaFechaFixtureGrupos
          titulo={
            todasLasFechasCompletas
              ? `Fecha ${fechaTrabajo}`
              : modoFixture === 'manual'
                ? `Fecha ${fechaTrabajo}`
                : 'Primera fecha'
          }
          partidosFechaActual={partidosFechaActual}
          partidosFaltantes={puedeEditar ? partidosFaltantes : []}
          equipos={equiposDisponibles}
          torneo={torneo}
          mostrarZona={mostrarZona}
          todasLasFechasCompletas={todasLasFechasCompletas}
          puedeEditar={puedeEditar}
          partidosEnEdicion={partidosEnEdicion}
          partidoGrabandose={partidoGrabandose}
          erroresFilaPartido={erroresFilaPartido}
          onPartidoElegido={onPartidoElegido}
          onEditarPartido={onEditarPartido}
          onEliminarPartido={onEliminarPartido}
          onPartidoEnEdicionChange={edicion.actualizarEdicion}
          onGuardarEdicion={onGuardarEdicion}
          onCancelarEdicion={edicion.cancelarEdicion}
          onPartidoChange={onPartidoChange}
          onGuardarPartido={onGuardarPartido}
          onCancelarPartido={onCancelarPartido}
        />
      )}

      {modoFixture !== 'automatico' &&
        puedeEditar &&
        (partidosFaltantes.length > 0 || Object.keys(partidosEnEdicion).length > 0) && (
          <div className={styles.acciones}>
            <button
              onClick={onGuardarTodos}
              disabled={isGrabando || isGrabandoPatch}
              className={styles.btnGuardarTodos}
            >
              Guardar todos los partidos editados
            </button>
            <MensajeGrabando
              isGrabando={isGrabando || isGrabandoPatch}
              entidad="partido"
              plural={true}
              estilo={styles.mensajeGrabandoTodos}
            />
          </div>
        )}

      <MensajeCargando isCargando={isCargando || isGenerandoPreview} />
      <MensajeGrabando
        isGrabando={isConfirmandoFixture}
        entidad="fixture"
        estilo={styles.mensajeGrabandoTodos}
      />
      <MensajeError errores={erroresGetLista} />
      <MensajeError errores={erroresPost} />
      <MensajeError errores={erroresPatch} />
      <MensajeError errores={erroresFixture} />
    </div>
  );
};

export default FixtureEtapaGrupos;
